import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { BadgeOutlined as RoleIcon, GroupAddOutlined as NewTeamIcon, GroupsOutlined as TeamIcon, PersonAddAlt1Outlined as AddPersonIcon } from '@mui/icons-material';
import MultiSelectFilter, { barButtonSx, SearchFilter } from '../monthly/MultiSelectFilter';
import { AddMemberPanel, MemberPanel, PersonAvatar, RoleChip, TeamPanel } from './PeoplePanels';
import { cardSx, GREEN, GREEN_SOFT, INK, LINE, MUTED } from '../home/homeTheme';
import { fetchProjectPeople } from '../../utils/projectPeopleService';

const EMPTY = { members: [], roles: [], teams: [] };
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const headSx = { fontSize: 12, fontWeight: 700, color: MUTED, bgcolor: 'var(--rt-paper)', py: 1, borderBottom: `1px solid ${LINE}`, whiteSpace: 'nowrap' };
const cellSx = { fontSize: 13, color: INK, py: 0.8, borderBottom: `1px solid ${LINE}` };
// Ordine dei ruoli: dal Service Manager al Team member
const ROLE_ORDER = { S: 0, J: 1, T: 2, C: 3 };

/**
 * Scheda People: persone del progetto con ruolo e team in un'unica tabella, team a fianco.
 * Persona, team e pannelli di creazione sono nell'URL (?person=ID, ?team=ID, ?new=person|team).
 */
export default function ProjectPeople({ projectId, canEdit }) {
  const [params, setParams] = useSearchParams();
  const personId = params.get('person');
  const teamId = params.get('team');
  const adding = params.get('new');

  const [refreshToken, setRefreshToken] = useState(0);
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchProjectPeople(projectId)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(`Unable to load the people of this project: ${e.message}`))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId, refreshToken]);

  const { members, roles, teams } = data;
  // Team di ogni persona (una persona sta in un solo team del progetto)
  const teamOf = useMemo(() => {
    const map = {};
    teams.forEach((t) => t.members.forEach((m) => (map[String(m.user_id)] = map[String(m.user_id)] || t)));
    return map;
  }, [teams]);
  const withoutTeam = members.filter((m) => !teamOf[String(m.user_id)]).length;

  // ---- Filtri e ordinamento ----
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState([]);
  const [teamFilter, setTeamFilter] = useState([]);
  const [sort, setSort] = useState({ key: 'role', dir: 'asc' });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = members.filter((m) => {
      if (roleFilter.length && !roleFilter.includes(m.project_role_id)) return false;
      if (teamFilter.length && !teamFilter.includes(teamOf[String(m.user_id)]?.id ?? 'none')) return false;
      return !q || [m.username, m.email].some((v) => String(v || '').toLowerCase().includes(q));
    });
    const byName = (a, b) => String(a.username).localeCompare(String(b.username));
    const sorters = {
      name: byName,
      role: (a, b) => (ROLE_ORDER[a.role_code] ?? 9) - (ROLE_ORDER[b.role_code] ?? 9),
      team: (a, b) => String(teamOf[String(a.user_id)]?.name || '~').localeCompare(String(teamOf[String(b.user_id)]?.name || '~')),
      added: (a, b) => String(a.created_at || '').localeCompare(String(b.created_at || '')),
    };
    list.sort((a, b) => sorters[sort.key](a, b) || byName(a, b));
    if (sort.dir === 'desc') list.reverse();
    return list;
  }, [members, search, roleFilter, teamFilter, sort, teamOf]);

  // ---- Pannelli ----
  const setPanel = (key, value) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      ['person', 'team', 'new'].forEach((k) => next.delete(k));
      if (key) next.set(key, String(value));
      return next;
    });
  const closePanel = () => setPanel(null);
  const onChanged = (message, options = {}) => {
    setSnack(message);
    setRefreshToken((t) => t + 1);
    if (options.openTeam) setPanel('team', options.openTeam);
    else if (options.close || 'openTeam' in options) closePanel();
  };
  const openMember = members.find((m) => String(m.user_id) === String(personId)) || null;
  const openTeam = teams.find((t) => String(t.id) === String(teamId)) || null;

  const sortHeader = (key, label) => (
    <TableCell sx={headSx} sortDirection={sort.key === key ? sort.dir : false}>
      <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : 'asc'} onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  if (loading) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="success" />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <SearchFilter value={search} onChange={setSearch} placeholder="Search name or email" width={260} />
        <MultiSelectFilter
          label="Roles"
          allLabel="All roles"
          icon={<RoleIcon />}
          options={roles.map((r) => ({ id: r.id, name: r.name }))}
          value={roleFilter}
          onChange={setRoleFilter}
          width={220}
        />
        <MultiSelectFilter
          label="Teams"
          allLabel="All teams"
          icon={<TeamIcon />}
          options={[...teams.map((t) => ({ id: t.id, name: t.name, caption: `${t.members.length} members` })), { id: 'none', name: 'No team', caption: `${withoutTeam} people` }]}
          value={teamFilter}
          onChange={setTeamFilter}
          width={220}
        />
        <Box sx={{ flex: 1 }} />
        {canEdit && (
          <>
            <Button variant="outlined" startIcon={<NewTeamIcon />} onClick={() => setPanel('new', 'team')} sx={barButtonSx}>
              New team
            </Button>
            <Button variant="contained" color="success" startIcon={<AddPersonIcon />} onClick={() => setPanel('new', 'person')} sx={{ ...barButtonSx, color: '#fff', bgcolor: GREEN, boxShadow: 'none', fontWeight: 700 }}>
              Add person
            </Button>
          </>
        )}
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' }, gap: 2, alignItems: 'start' }}>
        {/* Persone */}
        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 3, bgcolor: 'var(--rt-paper)', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', minHeight: 240 }}>
            <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 620 }}>
              <colgroup>
                <col />
                <col style={{ width: 170 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 120 }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  {sortHeader('name', 'Person')}
                  {sortHeader('role', 'Role')}
                  {sortHeader('team', 'Team')}
                  {sortHeader('added', 'Added')}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((m) => {
                  const team = teamOf[String(m.user_id)];
                  return (
                    <TableRow
                      key={m.user_id}
                      hover
                      selected={String(m.user_id) === String(personId)}
                      onClick={() => setPanel('person', m.user_id)}
                      sx={{ cursor: 'pointer', '&.Mui-selected, &.Mui-selected:hover': { bgcolor: GREEN_SOFT } }}
                    >
                      <TableCell sx={cellSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                          <PersonAvatar name={m.username} size={30} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>
                              {m.username}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
                              {m.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <RoleChip code={m.role_code} name={m.role_name} />
                      </TableCell>
                      <TableCell sx={{ ...cellSx, color: team ? INK : MUTED }}>
                        <Typography noWrap sx={{ fontSize: 13, color: 'inherit' }}>
                          {team?.name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...cellSx, color: MUTED, whiteSpace: 'nowrap' }}>{fmtDate(m.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ ...cellSx, py: 4, textAlign: 'center', color: MUTED }}>
                      {members.length ? 'No people match these filters.' : 'No one is on this project yet.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${LINE}`, bgcolor: 'var(--rt-subtle-2)' }}>
            <Typography sx={{ fontSize: 13, color: MUTED }}>
              {rows.length} {rows.length === 1 ? 'person' : 'people'}
              {rows.length !== members.length ? ` of ${members.length}` : ''} · click a row to change role or team
            </Typography>
          </Box>
        </Box>

        {/* Team */}
        <Box sx={{ ...cardSx, p: 0, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: `1px solid ${LINE}` }}>
            <TeamIcon sx={{ fontSize: 20, color: MUTED }} />
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK, flex: 1 }}>Teams · {teams.length}</Typography>
          </Box>
          {!teams.length ? (
            <Typography sx={{ fontSize: 13.5, color: MUTED, px: 2, py: 2 }}>No teams yet.</Typography>
          ) : (
            <Box sx={{ maxHeight: 'calc(100vh - 360px)', overflowY: 'auto' }}>
              {teams.map((t) => (
                <ButtonBase
                  key={t.id}
                  onClick={() => setPanel('team', t.id)}
                  sx={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    px: 2,
                    py: 1.1,
                    borderBottom: `1px solid ${LINE}`,
                    bgcolor: String(t.id) === String(teamId) ? GREEN_SOFT : 'transparent',
                    '&:hover': { bgcolor: String(t.id) === String(teamId) ? GREEN_SOFT : 'var(--rt-hover)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: INK, flex: 1 }}>
                      {t.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED, whiteSpace: 'nowrap' }}>
                      {t.members.length} member{t.members.length === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                  {t.description && (
                    <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
                      {t.description}
                    </Typography>
                  )}
                </ButtonBase>
              ))}
            </Box>
          )}
          {withoutTeam > 0 && (
            <ButtonBase onClick={() => setTeamFilter(['none'])} sx={{ display: 'block', width: '100%', textAlign: 'left', px: 2, py: 1.1, '&:hover': { bgcolor: 'var(--rt-hover)' } }}>
              <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                {withoutTeam} {withoutTeam === 1 ? 'person' : 'people'} without a team · <Box component="span" sx={{ color: 'var(--rt-green-dark)', fontWeight: 600 }}>show</Box>
              </Typography>
            </ButtonBase>
          )}
        </Box>
      </Box>

      <MemberPanel
        open={!!openMember}
        member={openMember}
        team={openMember ? teamOf[String(openMember.user_id)] : null}
        roles={roles}
        teams={teams}
        projectId={projectId}
        canEdit={canEdit}
        onClose={closePanel}
        onChanged={onChanged}
      />
      <AddMemberPanel open={canEdit && adding === 'person'} members={members} roles={roles} teams={teams} projectId={projectId} onClose={closePanel} onChanged={onChanged} />
      <TeamPanel
        open={!!openTeam || (canEdit && adding === 'team')}
        team={adding === 'team' ? null : openTeam}
        creating={adding === 'team'}
        members={members}
        teams={teams}
        projectId={projectId}
        canEdit={canEdit}
        onClose={closePanel}
        onChanged={onChanged}
      />

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack(null)} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
